/**
 * MenuRegisterPresenter
 * --
 */
import React, { useRef, useState, useEffect, useCallback } from 'react';
import {
  Content,
  Row,
  Col,
  Descriptions,
  Text,
  Collapse,
  ModalLayout,
  UploadFile,
  DragAndDrop,
} from 'components';
import { Avatar, Button, Input, Radio } from 'antd';
import MessageAlert, { stringToMoneyFormat } from 'utils';
import { XAxis, YAxis, BarChart, Bar, Tooltip } from 'recharts';
import { CloseOutlined } from '@ant-design/icons';

const OptionForm = ({ data, onChange, onRemove }) => {
  const [inputValue, setInputValue] = useState({ ...data });
  const [optionValue, setOptionValue] = useState({
    option_name: '',
    option_price: 0,
    default: false,
  });

  const styles = {
    label: {
      fontSize: 11,
      fontWeight: 600,
      color: ' #7E7E7E',
      textAlign: 'left',
    },

    flexBox: {
      display: 'flex',
      // alignItems: 'center',
    },

    button: {
      width: '100%',
      height: '100%',
    },
  };

  /**
   * 데이터 바인딩 함수
   * --
   */
  const handleChange = useCallback((t, v, callback = null) => {
    setInputValue((prevValue) => {
      if (prevValue[t] === v) return prevValue;

      const newData = { ...prevValue, [t]: v };

      if (callback) callback(v);

      return newData;
    });
  }, []);

  /**
   * 옵션 항목 추가
   * --
   */
  const handleOptionList = (type, i = null) => {
    const { options } = inputValue;

    if (type === 'add') {
      const { option_name, option_price } = optionValue;
      if (!option_name) {
        MessageAlert.warning('옵션 항목명을 입력해주세요.');
        return;
      }

      if (option_price !== 0 && !option_price) {
        MessageAlert.warning('옵션 추가 금액을 입력해주세요.');
        return;
      }

      handleChange(
        'options',
        options?.length
          ? [...options, optionValue]
          : [
              ...options,
              {
                ...optionValue,
                default: true,
              },
            ],
      );
      setOptionValue({
        option_name: '',
        option_price: 0,
        default: false,
      });
    } else {
      const { options } = inputValue;

      const newOptions = options.filter((_, _index) => _index !== i);

      const findDefault = newOptions.find((item) => item.default);

      if (findDefault) {
        handleChange('options', newOptions);
      } else {
        let newO = [...newOptions];
        newO[0] = {
          ...newO[0],
          default: true,
        };
        handleChange('options', newO);
      }
    }
  };

  /**
   * 옵션 항목 기본값 변경
   * --
   */
  const handleDefaultOption = (arr, i) => {
    const newArr = arr.map((_item, _index) => ({
      ..._item,
      default: _index === i ? true : false,
    }));

    handleChange('options', newArr);
  };

  useEffect(() => {
    onChange(inputValue);
    // eslint-disable-next-line
  }, [inputValue]);

  return (
    <Content maxWidth={'100%'} padding={0} style={{ marginBottom: 20 }}>
      {/* 옵션 명 입력 */}
      <Row gutter={[10, 0]}>
        <Col x={8}>
          <Text style={{ ...styles.label }}>옵션명</Text>
        </Col>
        <Col x={8}>
          <Text style={{ ...styles.label }}>필수옵션 설정</Text>
        </Col>
        <Col x={8}></Col>

        <Col x={8}>
          <Input
            value={data.option_title}
            size="large"
            count={{ max: 30, show: true }}
            placeholder="옵션명을 입력해주세요"
            onChange={(e) => handleChange('option_title', e.target.value)}
          />
        </Col>
        <Col x={8} style={{ display: 'flex', alignItems: 'center' }}>
          <Radio.Group
            value={data.required}
            onChange={(e) => handleChange('required', e.target.value)}
          >
            <Radio value={true}>필수옵션</Radio>
            <Radio value={false}>선택옵션</Radio>
          </Radio.Group>
        </Col>
        <Col
          x={8}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <Button icon={<CloseOutlined />} onClick={() => onRemove()} />
        </Col>
      </Row>

      {/* 옵션 항목 추가 */}
      <Row gutter={[10, 10]} style={{ marginTop: 10 }}>
        <Col x={8}>
          <Row gutter={[8, 0]}>
            <Col x={10}>
              <Text style={{ ...styles.label }}>옵션항목</Text>
            </Col>
            <Col x={8}>
              <Text style={{ ...styles.label }}>옵션항목 금액</Text>
            </Col>
            <Col x={6}></Col>
          </Row>
        </Col>
        <Col x={8}></Col>
        <Col x={8}></Col>
        <Col x={8}>
          <Row gutter={[8, 0]}>
            <Col x={10}>
              <Input
                size="large"
                value={optionValue.option_name}
                onChange={(e) =>
                  setOptionValue((prev) => ({
                    ...prev,
                    option_name: e.target.value,
                  }))
                }
              />
            </Col>
            <Col x={8}>
              <Input
                size="large"
                value={optionValue.option_price}
                onChange={(e) =>
                  setOptionValue((prev) => ({
                    ...prev,
                    option_price: e.target.value,
                  }))
                }
              />
            </Col>
            <Col x={6}>
              <Button
                style={{
                  ...styles.button,
                  borderColor: '#0066FF',
                  color: '#0066FF',
                }}
                onClick={() => handleOptionList('add')}
              >
                +추가
              </Button>
            </Col>
          </Row>
        </Col>
        <Col x={8}></Col>
        <Col x={8}></Col>
      </Row>

      {/* 옵션 항목 */}
      <Row gutter={[10, 10]} style={{ marginTop: 10 }}>
        {inputValue.options?.map((_item, _index, arr) => (
          <React.Fragment key={`option_item_${_index}`}>
            <Col x={8}>
              <Row gutter={[8, 0]}>
                <Col x={10}>
                  <Input
                    size="large"
                    value={_item.option_name}
                    onChange={(e) =>
                      setOptionValue((prev) => ({
                        ...prev,
                        option_name: e.target.value,
                      }))
                    }
                  />
                </Col>
                <Col x={8}>
                  <Input
                    size="large"
                    value={_item.option_price}
                    onChange={(e) =>
                      setOptionValue((prev) => ({
                        ...prev,
                        option_price: e.target.value,
                      }))
                    }
                  />
                </Col>
                <Col x={6}>
                  <Button
                    style={{
                      ...styles.button,
                      borderColor: '#EB2A2A',
                      color: '#EB2A2A',
                    }}
                    onClick={() => handleOptionList('remove', _index)}
                  >
                    -삭제
                  </Button>
                </Col>
              </Row>
            </Col>
            <Col x={8} style={{ display: 'flex', alignItems: 'center' }}>
              <Radio
                name={`option_default_${_index}`}
                checked={_item.default}
                onChange={(e) => handleDefaultOption(arr, _index)}
              >
                기본값
              </Radio>
            </Col>
            <Col x={8}></Col>
          </React.Fragment>
        ))}
      </Row>
    </Content>
  );
};

const MenuRegisterPresenter = (props) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  const {
    data,
    selectedItem,
    isOpened,
    modalType,
    files,
    options,
    onSelect,
    onModal,
    onChange,
    onFileChange,
    onOptionList,
    onOptionChange,
    onDrop,
    onUpdateOrder,
  } = props;

  /* ===== STATE ===== */
  const chartRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(0);

  /* ===== VARIABLE ===== */
  const styles = {
    button: {
      width: '50%',
      height: 32,
      fontSize: 13,
      fontWeight: 600,
      borderRadius: 0,
    },
  };

  const optionTemp = {
    option_title: '',
    required: true,
    options: [],
  };

  /* ===== FUNCTION ===== */
  /**
   * 한달치 판매량 데이터 정제
   * --
   */
  const generateSalesData = () => {
    const salesData = [];
    const today = new Date();

    for (let i = 0; i < 30; i++) {
      const date = new Date();
      date.setDate(today.getDate() - i);
      const day = `${String(date.getMonth() + 1).padStart(2, '0')}-${String(
        date.getDate(),
      ).padStart(2, '0')}`;
      const sales = Math.floor(Math.random() * 5000); // 0에서 4999까지의 랜덤 판매량
      salesData.push({ day, sales });
    }

    return salesData.reverse(); // 최근 날짜가 마지막에 오도록 배열을 뒤집기
  };
  const salesStatistic = generateSalesData();

  /* ===== HOOKS ===== */
  useEffect(() => {
    const call = (e) => {
      setChartWidth(chartRef.current.clientWidth);
    };

    if (chartRef.current) call();

    window.addEventListener('resize', call);

    return () => {
      window.removeEventListener('resize', call);
    };
  }, [chartRef]);

  /* ===== RENDER ===== */
  return (
    <>
      <Content maxWidth={'100%'} padding={0}>
        <Row>
          <Col x={6}>
            <Content maxWidth={'100%'} padding={0} style={{ height: '100%' }}>
              <Content
                maxWidth={'100%'}
                padding={0}
                backgroundColor="#FAFAFA"
                style={{
                  minHeight: 800,
                  height: '100%',
                  overflow: 'auto',
                  borderRight: '1px solid #F0F0F0',
                  position: 'relative',
                  top: 0,
                  left: 0,
                }}
              >
                <Content maxWidth={'100%'} padding={8}>
                  <Button
                    style={{ ...styles.button }}
                    onClick={() => onModal(isOpened, 'category')}
                  >
                    카테고리 추가
                  </Button>
                  <Button
                    type="primary"
                    style={{ ...styles.button }}
                    onClick={() => onModal(isOpened, 'menu')}
                  >
                    메뉴 추가
                  </Button>
                </Content>
                {data.map((_item) => (
                  <Collapse
                    key={`category_${_item.category_product_id}`}
                    title={_item.category_product_name}
                    defaultOpened
                    titleStyle={{
                      fontWeight: 500,
                      color: '#4A4A4A',
                      background: '#D4D4D4',
                    }}
                  >
                    {_item.menus.map((_subitem) => (
                      <div
                        key={`menu_${_subitem.menu_id}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: 8,
                          cursor: 'pointer',
                          border:
                            selectedItem?.menu_id === _subitem.menu_id
                              ? '1px solid #2394E4'
                              : '1px solid #EFEFEF',
                          borderBottom:
                            selectedItem?.menu_id === _subitem.menu_id
                              ? '1px solid #2394E4'
                              : '1px solid #DFDFDF',
                          background:
                            selectedItem?.menu_id === _subitem.menu_id
                              ? 'rgba(28, 107, 244, 0.3)'
                              : '#EFEFEF',
                        }}
                        onClick={() => onSelect(_subitem)}
                      >
                        <div>
                          <Avatar shape="square" size={30} />
                        </div>
                        <div style={{ paddingLeft: 7 }}>
                          <Text size={12} weight={600} color={'#4E4E4E'}>
                            {_subitem.menu_name}
                          </Text>
                          <Text size={11} weight={500} color={'#4E4E4E'}>
                            {stringToMoneyFormat(_subitem.price, '원')}
                          </Text>
                        </div>
                      </div>
                    ))}
                  </Collapse>
                ))}
                <Content
                  maxWidth={'100%'}
                  padding={8}
                  backgroundColor="none"
                  style={{ position: 'absolute', bottom: 0, left: 0 }}
                >
                  <Button
                    style={{
                      width: '100%',
                      height: 32,
                      fontSize: 13,
                      fontWeight: 500,
                    }}
                    onClick={() => onModal(isOpened, 'order')}
                  >
                    순서편집
                  </Button>
                </Content>
              </Content>
            </Content>
          </Col>
          <Col x={18}>
            <Content maxWidth={'100%'} padding={20}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 10,
                }}
              >
                <Text size={14} weight={600} color={'#404040'}>
                  메뉴정보
                </Text>
                <Button
                  type="primary"
                  style={{ background: 'var(--primary-color)', width: 73 }}
                >
                  편집
                </Button>
              </div>
              <Descriptions
                align="left"
                column={3}
                items={[
                  {
                    key: `1`,
                    label: '메뉴명',
                    children: selectedItem.menu_name
                      ? selectedItem.menu_name
                      : '-',
                  },
                  {
                    key: `2`,
                    label: '메뉴번호',
                    children: selectedItem.menu_code
                      ? selectedItem.menu_code
                      : '-',
                  },
                  {
                    key: `3`,
                    label: '단가',
                    children: selectedItem.price
                      ? stringToMoneyFormat(selectedItem.price, '원')
                      : '-',
                  },
                  {
                    key: `4`,
                    label: '비고',
                    children: selectedItem.menu_content
                      ? selectedItem.menu_content
                      : '-',
                    span: 3,
                  },
                  {
                    key: `5`,
                    label: '기타옵션',
                    children: !selectedItem.menu_option
                      ? '-'
                      : Object.entries(selectedItem.menu_option).map(
                          ([key, value], _index, _arr) => (
                            <div
                              key={`option_${_index}`}
                              style={{
                                padding: '10px 20px',
                                borderBottom:
                                  _arr.length === _index + 1
                                    ? 'none'
                                    : '1px solid rgba(112, 115, 124, 0.2)',
                              }}
                            >
                              <Text size={13} weight={600} color={'#454545'}>
                                {key}
                              </Text>
                              <Radio.Group style={{ marginTop: 8 }} value={''}>
                                {value.map((_item) => (
                                  <Radio value={_item}>
                                    {_item.option_name}
                                  </Radio>
                                ))}
                              </Radio.Group>
                            </div>
                          ),
                        ),
                    span: 3,
                    style: { padding: 0 },
                  },
                  {
                    key: `6`,
                    label: '이미지',
                    children: (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          padding: '20px 0px',
                        }}
                      >
                        {selectedItem.files?.map((_, _index) => (
                          <Avatar
                            key={`img_${_index}`}
                            size={100}
                            shape="square"
                            style={{ marginRight: 10 }}
                            src={_}
                          />
                        ))}
                      </div>
                    ),
                    span: 3,
                  },
                  {
                    key: `7`,
                    label: '매출',
                    children: (
                      <div style={{ padding: '20px 0px' }}>
                        <ul style={{ display: 'flex', alignItems: 'center' }}>
                          <li
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              paddingRight: 40,
                            }}
                          >
                            <span
                              style={{
                                width: 5,
                                height: 36,
                                background: '#0F6CBD',
                                marginRight: 5,
                              }}
                            ></span>
                            <div>
                              <Text
                                size={12}
                                color={'#424242'}
                                style={{ lineHeight: 1.25 }}
                              >
                                매출
                              </Text>
                              <Text
                                size={16}
                                weight={600}
                                color={'#424242'}
                                style={{ lineHeight: 1.25 }}
                              >
                                8,423,000원
                              </Text>
                            </div>
                          </li>
                          <li style={{ display: 'flex', alignItems: 'center' }}>
                            <span
                              style={{
                                width: 5,
                                height: 36,
                                background: '#C50F1F',
                                marginRight: 5,
                              }}
                            ></span>
                            <div>
                              <Text
                                size={12}
                                color={'#424242'}
                                style={{ lineHeight: 1.25 }}
                              >
                                판매비율
                              </Text>
                              <Text
                                size={16}
                                weight={600}
                                color={'#424242'}
                                style={{ lineHeight: 1.25 }}
                              >
                                21%
                              </Text>
                            </div>
                          </li>
                        </ul>
                        <div ref={chartRef} style={{ marginTop: 20 }}>
                          <BarChart
                            width={chartWidth}
                            height={300}
                            data={salesStatistic}
                          >
                            <XAxis
                              dataKey={'day'}
                              stroke="#686C73"
                              // tickFormatter={(d) => moment(d).format('MM-DD')}
                            />
                            <YAxis stroke="#686C73" />
                            <Tooltip />
                            <Bar
                              dataKey={'sales'}
                              fill={'var(--primary-color)'}
                            />
                          </BarChart>
                        </div>
                      </div>
                    ),
                    span: 3,
                  },
                ]}
              />
            </Content>
          </Col>
        </Row>
      </Content>

      {/* 카테고리 추가 */}
      <ModalLayout
        type="modal"
        centered
        title={'카테고리 추가'}
        open={isOpened && modalType === 'category'}
        onCancel={() => onModal(isOpened)}
        closable
        width={500}
        footer={
          <Content
            maxWidth={'100%'}
            padding={0}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Button onClick={() => onModal(isOpened)}>닫기</Button>
            <Button type="primary">저장</Button>
          </Content>
        }
      >
        <Content maxWidth={'100%'} padding={'30px 0px'}>
          <Row>
            <Col x={24}>
              <Content maxWidth={'100%'} padding={0}>
                <div>
                  <Input
                    size="large"
                    count={{
                      show: true,
                      max: 30,
                    }}
                    placeholder="카테고리명을 입력해주세요"
                    onChange={(e) =>
                      onChange('category_product_name', e.target.value)
                    }
                  />
                </div>
                <div style={{ marginTop: 10 }}>
                  <Input.TextArea
                    size="large"
                    count={{
                      show: true,
                      max: 130,
                    }}
                    placeholder="설명을 입력해주세요"
                    onChange={(e) =>
                      onChange('category_product_description', e.target.value)
                    }
                  />
                </div>
              </Content>
            </Col>
          </Row>
        </Content>
      </ModalLayout>

      {/* 메뉴 추가
      <ModalLayout
        type="modal"
        centered
        title={'메뉴 츄가'}
        open={isOpened && modalType === 'menu'}
        onCancel={() => onModal(isOpened)}
        closable
        width={'60%'}
        footer={
          <Content
            maxWidth={'100%'}
            padding={0}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Button onClick={() => onModal(isOpened)}>닫기</Button>
            <Button type="primary">저장</Button>
          </Content>
        }
      >
        <Content maxWidth={'100%'} padding={'30px 0px'}>
          <Row>
            <Col x={24}>
              <Content maxWidth={'100%'} padding={0}>
                <Descriptions
                  items={[
                    {
                      key: 'menu_name',
                      label: '메뉴명',
                      children: (
                        <Input
                          size="large"
                          count={{
                            show: true,
                            max: 30,
                          }}
                          placeholder="메뉴명을 입력해주세요"
                          onChange={(e) =>
                            onChange('menu_name', e.target.value)
                          }
                        />
                      ),
                    },
                    {
                      key: 'price',
                      label: '단가',
                      children: (
                        <Input
                          size="large"
                          placeholder="금액을 입력해주세요"
                          onChange={(e) => onChange('price', e.target.value)}
                        />
                      ),
                    },
                    {
                      key: 'menu_content',
                      label: '설명',
                      span: 2,
                      children: (
                        <div style={{ padding: '20px 0px' }}>
                          <Input.TextArea
                            size="large"
                            placeholder="설명을 입력해주세요"
                            count={{
                              show: true,
                              max: 130,
                            }}
                            style={{ minHeight: 78 }}
                            onChange={(e) =>
                              onChange('menu_content', e.target.value)
                            }
                          />
                        </div>
                      ),
                    },
                    {
                      key: 'menu_files',
                      label: '이미지',
                      span: 2,
                      children: (
                        <div style={{ padding: '20px 0px', display: 'flex' }}>
                          <UploadFile
                            files={files}
                            preview
                            multiple
                            max={5}
                            onChange={onFileChange}
                          />
                        </div>
                      ),
                    },
                    {
                      key: 'menu_option',
                      label: '옵션',
                      span: 2,
                      children: (
                        <div style={{ padding: '20px 0px' }}>
                          {options?.map((_item, _index) => (
                            <OptionForm
                              key={`option_form_${_index}`}
                              data={_item}
                              onChange={(d) => onOptionChange(d, _index)}
                              onRemove={() =>
                                onOptionList((prev) =>
                                  prev.filter((_, i) => _index !== i)
                                )
                              }
                            />
                          ))}
                          <div>
                            <Button
                              type="primary"
                              style={{ width: '100%' }}
                              onClick={() =>
                                onOptionList((prev) => [...prev, optionTemp])
                              }
                            >
                              옵션 등록
                            </Button>
                          </div>
                        </div>
                      ),
                    },
                  ]}
                />
              </Content>
            </Col>
          </Row>
        </Content>
      </ModalLayout> */}

      {/* 순서 편집 */}
      <ModalLayout
        type="modal"
        centered
        title={'순서편집'}
        open={isOpened && modalType === 'order'}
        onCancel={() => onModal(isOpened)}
        closable
        width={500}
        footer={
          <Content
            maxWidth={'100%'}
            padding={0}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Button onClick={() => onModal(isOpened)}>닫기</Button>
            <Button type="primary" onClick={() => onUpdateOrder()}>
              저장
            </Button>
          </Content>
        }
      >
        <Content maxWidth={'100%'} padding={'30px 0px'}>
          <Row>
            <Col x={24}>
              <Content maxWidth={'100%'} padding={0}>
                <DragAndDrop
                  items={data}
                  onDrop={(e) => onDrop(e)}
                  render={(item) => (
                    <Collapse
                      key={`category_${item.category_product_id}`}
                      title={item.category_product_name}
                      defaultOpened={true}
                      titleStyle={{
                        fontWeight: 500,
                        color: '#4A4A4A',
                        background: '#D4D4D4',
                      }}
                    >
                      <DragAndDrop
                        items={item.menus}
                        depth={1}
                        parentId={item.category_product_id}
                        onDrop={(e) => onDrop(e, 1, item.category_product_id)}
                        render={(subitem) => (
                          <div
                            key={`menu_${subitem.menu_id}`}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: 8,
                              cursor: 'pointer',
                              border: '1px solid #EFEFEF',
                              borderBottom: '1px solid #DFDFDF',
                              background: '#EFEFEF',
                            }}
                          >
                            <div>
                              <Avatar shape="square" size={30} />
                            </div>
                            <div style={{ paddingLeft: 7 }}>
                              <Text size={12} weight={600} color={'#4E4E4E'}>
                                {subitem.menu_name}
                              </Text>
                              <Text size={11} weight={500} color={'#4E4E4E'}>
                                {stringToMoneyFormat(subitem.price, '원')}
                              </Text>
                            </div>
                          </div>
                        )}
                      />
                    </Collapse>
                  )}
                />
              </Content>
            </Col>
          </Row>
        </Content>
      </ModalLayout>

      {/* 메뉴 추가 수정본*/}
      <ModalLayout
        type="modal"
        centered
        title={'메뉴 추가'}
        open={isOpened && modalType === 'menu'}
        onCancel={() => onModal(isOpened)}
        closable
        width={'60%'}
        footer={
          <Content
            maxWidth={'100%'}
            padding={0}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Button onClick={() => onModal(isOpened)}>닫기</Button>
            <Button type="primary">저장</Button>
          </Content>
        }
      >
        <Content maxWidth={'100%'} padding={'30px 0px'}>
          <Row>
            <Col x={24}>
              <Content maxWidth={'100%'} padding={0}>
                <Descriptions
                  items={[
                    {
                      key: 'menu_name',
                      label: '메뉴명',
                      children: (
                        <Input
                          size="large"
                          count={{
                            show: true,
                            max: 30,
                          }}
                          placeholder="메뉴명을 입력해주세요"
                          onChange={(e) =>
                            onChange('menu_name', e.target.value)
                          }
                        />
                      ),
                    },
                    {
                      key: 'price',
                      label: '단가',
                      children: (
                        <Input
                          size="large"
                          placeholder="금액을 입력해주세요"
                          onChange={(e) => onChange('price', e.target.value)}
                        />
                      ),
                    },
                    {
                      key: 'menu_content',
                      label: '설명',
                      span: 2,
                      children: (
                        <div style={{ padding: '20px 0px' }}>
                          <Input.TextArea
                            size="large"
                            placeholder="설명을 입력해주세요"
                            count={{
                              show: true,
                              max: 130,
                            }}
                            style={{ minHeight: 78 }}
                            onChange={(e) =>
                              onChange('menu_content', e.target.value)
                            }
                          />
                        </div>
                      ),
                    },
                    {
                      key: 'menu_files',
                      label: '이미지',
                      span: 2,
                      children: (
                        <div style={{ padding: '20px 0px', display: 'flex' }}>
                          <UploadFile
                            files={files}
                            preview
                            multiple
                            max={5}
                            onChange={onFileChange}
                          />
                        </div>
                      ),
                    },
                    {
                      key: 'menu_option',
                      label: '옵션',
                      span: 2,
                      children: (
                        <div style={{ padding: '20px 0px' }}>
                          {options?.map((_item, _index) => (
                            <OptionForm
                              key={`option_form_${_index}`}
                              data={_item}
                              onChange={(d) => onOptionChange(d, _index)}
                              onRemove={() =>
                                onOptionList((prev) =>
                                  prev.filter((_, i) => _index !== i),
                                )
                              }
                            />
                          ))}
                          <div>
                            <Button
                              type="primary"
                              style={{ width: '100%' }}
                              onClick={() =>
                                onOptionList((prev) => [...prev, optionTemp])
                              }
                            >
                              옵션 등록
                            </Button>
                          </div>
                        </div>
                      ),
                    },
                  ]}
                />
              </Content>
            </Col>
          </Row>
        </Content>
      </ModalLayout>
    </>
  );
};

export default MenuRegisterPresenter;
